import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLeagueApi } from '../hooks/useLeagueApi';

// Exported (not just the hooks) so a static host with no real league/auth
// context — ExampleDashboard — can supply a fixed value directly, instead of
// MyTeamProvider's real fetch-on-mount behaviour, which needs LeagueContext
// and Clerk auth neither of which /example has.
export const MyTeamContext = createContext(null);

export function MyTeamProvider({ children }) {
  const api = useLeagueApi();
  const [myTeamId, setMyTeamIdState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // The team arrives in the dashboard bundle; this shares the dashboard's own request.
    api.getDashboard()
      .then(data => { if (!cancelled) setMyTeamIdState(data?.my_team?.team_id ?? null); })
      .catch(() => { if (!cancelled) setMyTeamIdState(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  const setMyTeam = useCallback(async (teamId) => {
    setMyTeamIdState(Number(teamId));
    try {
      await api.setMyTeam(teamId);
    } catch (e) {
      console.error('Failed to persist team selection:', e);
    }
  }, [api]);

  const clearMyTeam = useCallback(async () => {
    setMyTeamIdState(null);
    try {
      await api.setMyTeam(null);
    } catch (e) {
      console.error('Failed to clear team selection:', e);
    }
  }, [api]);

  const previewTeam = useCallback((teamId) => {
    setMyTeamIdState(Number(teamId));
  }, []);

  return (
    <MyTeamContext.Provider value={{ myTeamId, setMyTeam, clearMyTeam, previewTeam, loading }}>
      {children}
    </MyTeamContext.Provider>
  );
}

export function useMyTeam() {
  const ctx = useContext(MyTeamContext);
  if (!ctx) throw new Error('useMyTeam must be used within MyTeamProvider');
  return ctx;
}

export function useMyTeamOptional() {
  return useContext(MyTeamContext);
}
